import { useState, useEffect } from 'react';
import { getMachines, createMachine, updateMachine, deleteMachine } from '../../services/erpApi';

export default function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    machineType: '',
    erpMachineId: '',
  });

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      const data = await getMachines();
      setMachines(data);
      setError(null);
    } catch (err) {
      setError('Failed to load machines');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMachine) {
        await updateMachine(editingMachine.id, formData);
      } else {
        await createMachine(formData);
      }
      setShowForm(false);
      setEditingMachine(null);
      setFormData({ name: '', description: '', machineType: '', erpMachineId: '' });
      loadMachines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save machine');
    }
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({
      name: machine.name,
      description: machine.description || '',
      machineType: machine.machineType || '',
      erpMachineId: machine.erpMachineId || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this machine?')) return;
    try {
      await deleteMachine(id);
      loadMachines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete machine');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMachine(null);
    setFormData({ name: '', description: '', machineType: '', erpMachineId: '' });
  };

  if (loading) {
    return <div className="text-gray-400">Loading machines...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Machines</h2>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white"
        >
          + Add Machine
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded p-3 text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingMachine ? 'Edit Machine' : 'Add Machine'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Machine Type</label>
                <select
                  value={formData.machineType}
                  onChange={(e) => setFormData({ ...formData, machineType: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select type...</option>
                  <option value="Lathe">Lathe</option>
                  <option value="Mill">Mill</option>
                  <option value="Grinder">Grinder</option>
                  <option value="Drill Press">Drill Press</option>
                  <option value="EDM">EDM</option>
                  <option value="Laser">Laser</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-500 focus:outline-none"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Simulator Machine ID
                  <span className="text-gray-500 ml-1">(links to simulator)</span>
                </label>
                <input
                  type="text"
                  value={formData.erpMachineId}
                  onChange={(e) => setFormData({ ...formData, erpMachineId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., SPM-3000-1"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white"
                >
                  {editingMachine ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Machines Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Simulator ID</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Operations</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Work Orders</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {machines.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No machines yet. Click "Add Machine" to create one.
                </td>
              </tr>
            ) : (
              machines.map((machine) => (
                <tr key={machine.id} className="hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">{machine.name}</td>
                  <td className="px-4 py-3 text-gray-400">{machine.machineType || '-'}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                    {machine.description || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {machine.erpMachineId ? (
                      <span className="px-2 py-1 bg-amber-900/30 text-amber-400 rounded text-sm">
                        {machine.erpMachineId}
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{machine._count?.partOperations || 0}</td>
                  <td className="px-4 py-3 text-gray-400">{machine._count?.workOrders || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEdit(machine)}
                      className="text-blue-400 hover:text-blue-300 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(machine.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
